import {EVENTS} from '@/services/analytics/constants';
import {mapAddToCartEvent} from '@/services/analytics/utils/add-to-cart-event-mapper';
import {mapProductViewEvent} from '@/services/analytics/utils/product-view-event-mapper';
import {mapErrorEvent} from '@/services/analytics/utils/error-event-mapper';
import {mapPageView} from '@/services/analytics/utils/page-view-mapper';

/**
 * Analytics service singleton class to handle analytics tracking.
 * This class provides methods for tracking page views and events.
 * These methods should be used for tracking page views and events consistently.
 * The `pageView` and `trackEvent` methods are the primary methods for tracking.
 * Other methods and utilities are provided for internal use and data transformation.
 */
export class Analytics {
  instance = null;
  dataLayer = null;
  adobeDataLayer = null;
  localItem = {};

  constructor() {
    const [dataLayer, adobeDataLayer] = Analytics._getAnalyticsResources();
    this.dataLayer = dataLayer;
    this.adobeDataLayer = adobeDataLayer;
  }

  static dataType = {
    PRODUCT: 'product-pricing',
    BUNDLE: 'bundle-product-pricing',
    CART_DELTA: 'cart-delta',
    CART: 'cart',
  };

  static getInstance() {
    if (!this.instance) {
      this.instance = new Analytics();
    }

    return this.instance;
  }

  static _getAnalyticsResources() {
    let dataLayer = globalThis.dataLayer;
    let adobeDataLayer = globalThis.adobeDataLayer;
    if (dataLayer == null) {
      dataLayer = {};
      console.warn('Unable to find data layer');
    }
    if (adobeDataLayer == null) {
      adobeDataLayer = [];
      console.warn('Unable to find adobe data layer');
    }

    return [dataLayer, adobeDataLayer];
  }

  static toItems(dataList, dataType, options = {}) {
    if (dataType === Analytics.dataType.BUNDLE) {
      return this._bundleDataListToItems(dataList, options);
    } else if (dataType === Analytics.dataType.PRODUCT) {
      return this._productPricingDataListToItems(dataList);
    } else if (dataType === Analytics.dataType.CART_DELTA) {
      return this._cartDeltaDataListToItems(dataList, options);
    } else if (dataType === Analytics.dataType.CART) {
      return this._cartDataListToItems(dataList);
    }
  }

  static _bundleDataListToItems(dataList, options = {}) {
    if (!dataList) return [];

    const {includeQty} = options;
    const skuToItem = new Map();
    for (const data of dataList) {
      if (!data?.prodConfig?.data) continue;

      for (const product of data.prodConfig.data) {
        const sku = product?.SKU || product?.sku;
        const item = this._productDataToItem(product, skuToItem.get(sku), data.prodConfig, data.bundleInfo, includeQty);
        skuToItem.set(sku, item);
      }
    }

    return Array.from(skuToItem.values());
  }

  static _productPricingDataListToItems(dataList) {
    if (!dataList) return [];

    const items = [];
    for (const data of dataList) {
      if (!data?.productPricingData) continue;

      for (const productPricingData of data.productPricingData) {
        if (!productPricingData?.products.length) continue;

        for (const product of productPricingData.products) {
          const item = Analytics._productToItem(product, data.tabTitle);
          items.push(item);
        }
      }
    }

    return items;
  }

  static _cartDeltaDataListToItems(dataList, options = {}) {
    const [origCart, cart] = dataList;
    if (!cart?.lineItems?.length) return [];

    const items = [];
    for (const lineItem of cart.lineItems) {
      const origLineItem = origCart?.lineItems?.find((origLineItem) => origLineItem?._id === lineItem._id);
      if (origLineItem?.quantity === lineItem?.quantity) continue;

      const item = this._lineItemToItem(lineItem, {
        currency: cart?.currency,
        quantity: lineItem.quantity - (origLineItem?.quantity || 0),
        bundle: options.bundle,
      });
      items.push(item);
    }

    return items;
  }

  static _cartDataListToItems(dataList) {
    const [cart] = dataList;
    if (!cart?.lineItems?.length) return [];

    const items = [];
    for (const lineItem of cart.lineItems) {
      const item = this._lineItemToItem(lineItem, {
        currency: cart?.currency,
      });
      items.push(item);
    }

    return items;
  }

  static _productToItem(product, tabTitle) {
    const productInfo = {
      sku: product.isFree && !product.dataLayerSku ? product.productTitle : product.dataLayerSku,
      name: product.productTitle,
      currency: product.currency,
      price: Analytics._formatPrice(product.displayPrice),
    };

    return {
      productInfo: productInfo,
      ...(tabTitle && {tabTitle}),
    };
  }

  static _productDataToItem(product, existingItem, prodConfig, bundleInfo, includeQty) {
    const prevProductInfo = existingItem?.productInfo;
    const productInfo = {
      sku: prevProductInfo?.sku || product.SKU || product.sku,
      name: prevProductInfo?.name || product.productTitle || product.productLable,
      price: prevProductInfo?.price || Analytics._formatPrice(product.price),
      currency: prevProductInfo?.currency || prodConfig?.currency,
      bundle: (prevProductInfo?.bundle || '') + (prevProductInfo?.bundle ? '|' : '') + (bundleInfo?.title || ''),
      ...(includeQty && product.qty && {quantity: product.qty}),
    };

    return {productInfo};
  }

  static _lineItemToItem(lineItem, options = {}) {
    const productInfo = {
      sku: lineItem.materialNumber?.toString(),
      name: lineItem.productName,
      price: lineItem.price,
      currency: options.currency,
      quantity: options.quantity || lineItem.quantity,
      ...(options.bundle && {bundle: options.bundle}),
    };

    return {productInfo};
  }

  static _formatPrice(price) {
    // Free or Contact Us products have undefined for price, change it to "0".
    return price ? price.replaceAll(/[^0-9.-]+/g, '') : '0';
  }

  static toStoreFront(zone) {
    return zone ? `${zone.toLowerCase()} - store` : null;
  }

  addPageInfo(pageData) {
    Object.assign(this.dataLayer, pageData);
  }

  addEvent(name, data) {
    this.adobeDataLayer.push({
      event: name,
      data: data,
    });
  }

  getPageItem(item) {
    return this.dataLayer[item];
  }

  setLocalItem(key, value) {
    this.localItem[key] = value;
  }

  getLocalItem(key) {
    return this.localItem[key];
  }

  pageView(pageType, pageSubType, source, options) {
    const data = mapPageView(source, options);

    this.addPageInfo({
      pageType,
      pageSubType,
      ...data,
    });
  }

  trackEvent(event, source, options) {
    let data = source;
    switch (event) {
      case EVENTS.CART_ADD:
      case EVENTS.CART_ADD_OPEN:
        data = mapAddToCartEvent(source, options);
        break;
      case EVENTS.PRODUCT_VIEW:
        data = mapProductViewEvent(source, options);
        break;
      case EVENTS.ERROR:
        data = mapErrorEvent(source, options);
        break;
      default:
        break;
    }
    this.addEvent(event, data);
  }
}

const analyticsService = Analytics.getInstance();
export {analyticsService};
